import { useEffect, useState } from 'react';
import { api, money, PAYMENT_RU, phoneFmt } from '../api';
import { useAuth } from '../auth';
import { useApi } from '../useApi';
import { Counter, Empty, ErrorBox, Loading, useToast } from '../ui';
import { Signature } from './Signature';
import { enqueue, flushQueue, newIdempotencyKey, readQueue } from './offline';

type Stop = {
  id: string;
  position: number;
  done: boolean;
  plannedGive: number;
  plannedTake: number;
  order: {
    id: string;
    number: string;
    status: string;
    totalKop: string;
    paymentMethod: string;
    comment: string | null;
    timeFrom: string | null;
    timeTo: string | null;
    items: Array<{ name: string; qty: number }>;
    delivery: { status: string; givenFull: number; takenEmpty: number } | null;
  };
  client: { id: string; name: string; phone: string; cachedBalanceKop: string; cachedBottles: number; comment: string | null };
  address: { street: string; house: string; flat: string | null; entrance: string | null; floor: number | null; comment: string | null; lat: number | null; lon: number | null };
};

type Today = {
  date: string;
  routes: Array<{ id: string; status: string; vehicle: string | null; loadedFull: number; stops: Stop[] }>;
};

/**
 * Приложение водителя.
 *
 * Работает с телефона на ходу: крупные кнопки, счётчики вместо клавиатуры,
 * ничего не теряется без связи. Экран всего два — список точек и одна точка.
 */
export function DriverApp() {
  const { me, logout } = useAuth();
  const { data, error, loading, reload } = useApi<Today>('/driver/today');
  const [open, setOpen] = useState<Stop | null>(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [queued, setQueued] = useState(readQueue().length);
  const [trip, setTrip] = useState(0);
  const toast = useToast();

  // Очередь уходит, как только вернулась сеть — водителю ничего нажимать
  // не нужно, он за рулём
  useEffect(() => {
    const sync = async () => {
      setOnline(navigator.onLine);
      if (!navigator.onLine || !readQueue().length) return;
      try {
        const res = await flushQueue();
        setQueued(res.left);
        if (res.sent) {
          toast.show(`Отправлено точек: ${res.sent}`);
          await reload();
        }
      } catch {
        /* сети всё ещё нет — попробуем на следующем событии */
      }
    };
    window.addEventListener('online', sync);
    window.addEventListener('offline', () => setOnline(false));
    const t = setInterval(sync, 30_000);
    void sync();
    return () => {
      window.removeEventListener('online', sync);
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Рейсов за день может быть несколько: машина вернулась на склад, догрузилась
   * и уехала второй раз. Показывался только первый — точки второго водитель не
   * видел вообще, хотя диспетчер их ему поставил.
   */
  const routes = data?.routes ?? [];
  const route = routes[Math.min(trip, Math.max(routes.length - 1, 0))];

  return (
    <div className="mobile">
      <header>
        <img src="/logo.png" alt="" />
        <div style={{ fontSize: 13, textAlign: 'right' }}>
          {me?.scope === 'staff' ? me.user.name : ''}
          <br />
          <button className="ghost" style={{ color: '#fff', padding: 0, fontSize: 12 }} onClick={logout}>
            выйти
          </button>
        </div>
      </header>

      {!online ? <div className="offline-bar">Нет связи. Точки сохраняются в телефоне.</div> : null}
      {queued > 0 ? (
        <div className="offline-bar" style={{ background: 'var(--blue)' }}>
          Ждут отправки: {queued}
        </div>
      ) : null}

      <div className="body">
        {loading ? (
          <Loading what="Гружу маршрут" />
        ) : error ? (
          <ErrorBox error={error} />
        ) : !route ? (
          <Empty>На сегодня маршрута нет. Он появится, когда диспетчер его соберёт.</Empty>
        ) : (
          <>
            {routes.length > 1 ? (
              <div className="tabs">
                {routes.map((r, i) => (
                  <button key={r.id} className={i === trip ? 'active' : ''} onClick={() => setTrip(i)}>
                    Рейс {i + 1} · {r.stops.filter((s) => s.done).length}/{r.stops.length}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="card">
              <div className="spread">
                <div>
                  <b>Маршрут на {data!.date}</b>
                  <div className="muted">
                    {route.vehicle ?? ''} · загружено {route.loadedFull} бут.
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>
                    {route.stops.filter((s) => s.done).length}/{route.stops.length}
                  </div>
                  <div className="muted" style={{ fontSize: 12 }}>точек</div>
                </div>
              </div>
              {route.status === 'PLANNED' ? (
                <button
                  className="primary big"
                  style={{ width: '100%', marginTop: 10 }}
                  onClick={async () => {
                    await api(`/driver/routes/${route.id}/start`, { body: {} });
                    toast.show('В рейсе');
                    await reload();
                  }}
                >
                  Выехал
                </button>
              ) : null}
              {route.status === 'ACTIVE' && route.stops.every((s) => s.done) ? (
                <button
                  className="primary big"
                  style={{ width: '100%', marginTop: 10 }}
                  onClick={async () => {
                    const empty = prompt('Сколько пустых бутылей сдаёте на склад?');
                    if (empty === null) return;
                    await api(`/driver/routes/${route.id}/close`, { body: { returnedEmpty: Number(empty) } });
                    toast.show('Маршрут сдан');
                    await reload();
                  }}
                >
                  Сдать маршрут
                </button>
              ) : null}
            </div>

            {route.stops.map((s) => (
              <div
                key={s.id}
                className={`stop ${s.done ? (s.order.delivery?.status === 'FAILED' ? 'failed' : 'done') : ''}`}
                onClick={() => !s.done && setOpen(s)}
              >
                <div className="spread">
                  <div>
                    <span className="num">{s.position}.</span>
                    <b>{s.client.name}</b>
                  </div>
                  {s.order.timeFrom ? <span className="badge">{s.order.timeFrom}–{s.order.timeTo}</span> : null}
                </div>
                <div className="muted">
                  {s.address.street}, {s.address.house}
                  {s.address.flat ? `, ${s.address.flat}` : ''}
                  {s.address.floor ? ` · ${s.address.floor} эт.` : ''}
                </div>
                {s.address.comment ? <div className="badge amber" style={{ marginTop: 4 }}>{s.address.comment}</div> : null}
                <div className="spread" style={{ marginTop: 8 }}>
                  <span>
                    Везём <b>{s.plannedGive}</b>, забрать <b>{s.plannedTake}</b>
                  </span>
                  <b>{money(s.order.totalKop)}</b>
                </div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {PAYMENT_RU[s.order.paymentMethod]}
                  {BigInt(s.client.cachedBalanceKop) < 0n
                    ? ` · долг ${money(-BigInt(s.client.cachedBalanceKop))}`
                    : ''}
                </div>
                {s.done ? (
                  <div className="badge green" style={{ marginTop: 6 }}>
                    {s.order.delivery?.status === 'FAILED'
                      ? 'не доставлено'
                      : `отдано ${s.order.delivery?.givenFull}, забрано ${s.order.delivery?.takenEmpty}`}
                  </div>
                ) : null}
              </div>
            ))}
          </>
        )}
      </div>

      {open ? (
        <StopScreen
          stop={open}
          onClose={() => setOpen(null)}
          onSaved={async (queuedNow) => {
            setOpen(null);
            setQueued(queuedNow);
            toast.show(queuedNow ? 'Сохранено в телефоне, уйдёт при связи' : 'Точка закрыта');
            await reload();
          }}
        />
      ) : null}
      {toast.node}
    </div>
  );
}

function StopScreen({
  stop,
  onClose,
  onSaved,
}: {
  stop: Stop;
  onClose: () => void;
  onSaved: (queuedLeft: number) => Promise<void>;
}) {
  const [given, setGiven] = useState(stop.plannedGive);
  const [taken, setTaken] = useState(stop.plannedTake);
  const [paid, setPaid] = useState(() => (Number(stop.order.totalKop) / 100).toFixed(2));
  const [method, setMethod] = useState(stop.order.paymentMethod);
  const [signature, setSignature] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const save = async (status: 'DONE' | 'PARTIAL' | 'FAILED', failReason?: string) => {
    setBusy(true);
    setError(null);
    const kop = Math.round(Number(paid.replace(',', '.')) * 100);
    const item = {
      orderId: stop.order.id,
      status,
      givenFull: status === 'FAILED' ? 0 : given,
      takenEmpty: status === 'FAILED' ? 0 : taken,
      paidKop: status === 'FAILED' ? '0' : String(Number.isFinite(kop) && kop > 0 ? kop : 0),
      paymentMethod: method,
      signature,
      comment: comment || null,
      failReason: failReason ?? null,
      idempotencyKey: newIdempotencyKey(stop.order.id),
      clientTime: new Date().toISOString(),
    };

    try {
      // Пробуем сразу; не вышло — кладём в очередь и идём дальше.
      // Водитель не должен стоять у подъезда и ждать сеть.
      await api('/driver/complete', { body: item });
      await onSaved(readQueue().length);
    } catch (e) {
      const isNetwork = e instanceof TypeError || !navigator.onLine;
      if (isNetwork) {
        enqueue(item);
        await onSaved(readQueue().length);
      } else {
        setError(e);
      }
    } finally {
      setBusy(false);
    }
  };

  const mapUrl = stop.address.lat
    ? `https://yandex.ru/maps/?rtext=~${stop.address.lat},${stop.address.lon}&rtt=auto`
    : `https://yandex.ru/maps/?text=${encodeURIComponent(`Иркутск, ${stop.address.street}, ${stop.address.house}`)}`;

  return (
    <div className="mobile" style={{ position: 'fixed', inset: 0, overflowY: 'auto', zIndex: 50 }}>
      <header>
        <button className="ghost" style={{ color: '#fff' }} onClick={onClose}>
          ← Назад
        </button>
        <span className="mono">{stop.order.number}</span>
      </header>

      <div className="body">
        <div className="card">
          <h2 style={{ marginBottom: 4 }}>{stop.client.name}</h2>
          <div className="muted">
            {stop.address.street}, {stop.address.house}
            {stop.address.flat ? `, ${stop.address.flat}` : ''}
            {stop.address.entrance ? ` · подъезд ${stop.address.entrance}` : ''}
            {stop.address.floor ? ` · ${stop.address.floor} эт.` : ''}
          </div>
          {stop.address.comment ? <div className="badge amber" style={{ marginTop: 6 }}>{stop.address.comment}</div> : null}
          {stop.client.comment ? <div className="badge" style={{ marginTop: 6 }}>{stop.client.comment}</div> : null}
          <div className="bigrow" style={{ marginTop: 12 }}>
            <a className="btn" href={`tel:+${stop.client.phone}`}>
              📞 {phoneFmt(stop.client.phone)}
            </a>
            <a className="btn" href={mapUrl} target="_blank" rel="noreferrer">
              🗺 Маршрут
            </a>
          </div>
        </div>

        <div className="card">
          <h3>Заказ</h3>
          {stop.order.items.map((i, n) => (
            <div key={n} className="spread">
              <span>{i.name}</span>
              <b>× {i.qty}</b>
            </div>
          ))}
          <div className="spread" style={{ marginTop: 8, fontSize: 18 }}>
            <span>К оплате</span>
            <b>{money(stop.order.totalKop)}</b>
          </div>
          {BigInt(stop.client.cachedBalanceKop) < 0n ? (
            <div className="badge red" style={{ marginTop: 6 }}>
              Долг клиента: {money(-BigInt(stop.client.cachedBalanceKop))}
            </div>
          ) : null}
          {BigInt(stop.client.cachedBalanceKop) > 0n ? (
            <div className="badge green" style={{ marginTop: 6 }}>
              Аванс: {money(stop.client.cachedBalanceKop)}
            </div>
          ) : null}
        </div>

        <div className="card">
          <h3>Тара</h3>
          <div className="row">
            <Counter label="Отдал полных" value={given} onChange={setGiven} />
            <Counter label="Забрал пустых" value={taken} onChange={setTaken} max={Math.max(20, stop.client.cachedBottles + given)} />
          </div>
          <div className="muted" style={{ marginTop: 8 }}>
            У клиента на руках было {stop.client.cachedBottles}. Станет{' '}
            <b>{stop.client.cachedBottles + given - taken}</b>.
          </div>
        </div>

        <div className="card">
          <h3>Деньги</h3>
          <div className="field">
            <label>Взял на месте, ₽</label>
            <input inputMode="decimal" value={paid} onChange={(e) => setPaid(e.target.value)} />
          </div>
          <div className="field">
            <label>Чем платят</label>
            <select value={method} onChange={(e) => setMethod(e.target.value)}>
              {['CASH', 'CARD_COURIER', 'BANK_TRANSFER', 'CREDIT'].map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_RU[m]}
                </option>
              ))}
            </select>
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            Ноль — значит «в долг»: сумма ляжет на баланс клиента.
          </div>
        </div>

        <div className="card">
          <h3>Подпись клиента</h3>
          <Signature onChange={setSignature} />
        </div>

        <div className="card">
          <div className="field">
            <label>Комментарий</label>
            <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="если что-то пошло не так" />
          </div>
          <button
            className="danger"
            style={{ width: '100%' }}
            disabled={busy}
            onClick={() => {
              const reason = prompt('Почему не доставили?', 'Не застали дома');
              if (reason) void save('FAILED', reason);
            }}
          >
            Не доставлено
          </button>
        </div>

        <ErrorBox error={error} />
      </div>

      <div className="sticky-actions">
        <button className="big" disabled={busy} onClick={() => void save('PARTIAL')}>
          Частично
        </button>
        <button className="primary big" disabled={busy} onClick={() => void save('DONE')}>
          Доставлено
        </button>
      </div>
    </div>
  );
}
